import { StatusChipVariant } from '@/components/legacy-ui/StatusChip';

/**
 * Status Chip Utility - Maps status values to StatusChip variants
 *
 * This utility provides consistent mapping from semantic status values
 * to StatusChip visual variants across all entity types in the application.
 *
 * Variant Mapping:
 * - info (Blue): Positive/active states (Active, Approved, Remediated, Completed, Online)
 * - high (Orange): Pending/warning states (Triaged, Pending, Inactive, Partial)
 * - critical (Red): Negative/error states (Opened, Expiring, Rejected, Failed, Offline)
 * - neutral (Green): Currently unused - reserved for future use
 * - muted (Gray): Inactive/frozen states (Frozen, Accepted, Deleted, Unknown, Queued, No scans)
 */

/**
 * Asset Status Variants
 */
export const assetStatusVariants: Record<string, StatusChipVariant> = {
  active: 'info',
  inactive: 'high',
  expiring: 'critical',
  frozen: 'muted',
  deleted: 'muted',
  pending: 'muted',
};

/**
 * Seed Status Variants
 */
export const seedStatusVariants: Record<string, StatusChipVariant> = {
  approved: 'info',
  'active passive': 'info',
  pending: 'high',
  rejected: 'critical',
  frozen: 'muted',
  deleted: 'muted',
};

/**
 * Vulnerability Status Variants
 */
export const vulnerabilityStatusVariants: Record<string, StatusChipVariant> = {
  triaged: 'high',
  opened: 'critical',
  remediated: 'info',
  accepted: 'muted',
  deleted: 'muted',
};

/**
 * Job/Scan Status Variants
 */
export const jobStatusVariants: Record<string, StatusChipVariant> = {
  completed: 'info',
  pass: 'info',
  failed: 'critical',
  fail: 'critical',
  running: 'info',
  queued: 'muted',
  partial: 'high',
  'no scans': 'muted',
};

/**
 * Agent Status Variants
 */
export const agentStatusVariants: Record<string, StatusChipVariant> = {
  online: 'info',
  offline: 'critical',
};

/**
 * Get variant for a given status label (case-insensitive)
 */
export function getVariantForStatus(
  label: string,
  statusMap: Record<string, StatusChipVariant>
): StatusChipVariant {
  const normalizedLabel = label.toLowerCase();
  return statusMap[normalizedLabel] ?? 'default';
}
